import type { CSSProperties, HTMLAttributes, ReactNode, RefObject } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../cn.js';
import { useDrawIn } from './internal/useDrawIn.js';

export type PanelAccent = 'accent' | 'success' | 'warning' | 'danger' | 'info' | 'none';

export interface PanelProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  title: ReactNode;
  /** A small tag beside the title, e.g. "HR portal". */
  tag?: ReactNode;
  /** Trailing header content — a "View all" link, a status badge. */
  action?: ReactNode;
  /** Top-edge colour stripe. Defaults to 'accent' — every panel gets one unless it's opted out
   *  with 'none'. Pass a semantic tone (e.g. 'danger' for an attention-needed panel) when the
   *  panel's own content already has a status; otherwise the default brand accent is correct. */
  accent?: PanelAccent;
  /** When present, the whole panel becomes a `react-router-dom` link (e.g. a KPI tile that opens
   *  the detail page it summarizes) instead of a plain `<section>` — same hover/focus-ring
   *  treatment the KPI cards hand-rolled before this prop absorbed that pattern. */
  to?: string;
  /** 'kpi' renders the title as an uppercase micro-label instead of the default display-font
   *  heading, matching the KPI tile/card headers elsewhere in the module. Defaults to 'default',
   *  which renders byte-identical to a Panel with no `variant` at all. */
  variant?: 'default' | 'kpi';
}

const ACCENT_BORDER: Record<PanelAccent, string> = {
  accent: 'border-t-accent',
  success: 'border-t-success',
  warning: 'border-t-warning',
  danger: 'border-t-danger',
  info: 'border-t-info',
  none: 'border-t-border',
};

/**
 * A titled card section — "Core services", "Approvals & updates", "This month" — repeated across
 * the home screen and its web/mobile variants. One component so the header row (title + tag +
 * action) always aligns the same way instead of being rebuilt slightly differently per screen.
 *
 * The coloured top border and hover lift are deliberate here rather than left to each call site:
 * a flat `border border-border` card reads as inert on every screen that uses it, and this is the
 * one component the whole Performance module's cards already share — so the fix belongs here
 * once, not sixty times across every panel usage.
 */
export function Panel({
  title,
  tag,
  action,
  accent = 'accent',
  to,
  variant = 'default',
  className,
  style,
  children,
  ...rest
}: PanelProps) {
  // Entrance: a one-time reveal when the card actually scrolls into view, not on mount — React
  // mounts the whole page at once, so a mount-gated reveal on anything below the fold finishes
  // fanning out seconds before a viewer ever scrolls to it, unseen. `useDrawIn` (shared with
  // BarChart/DonutChart/LineChart's own draw-in) reports `revealed` via IntersectionObserver on
  // this card's own root. Before that, the card sits at `opacity-0` (no slide/translate pre-set —
  // `animate-in`/`slide-in-from-bottom-2`'s own keyframe already carries its start offset, so there
  // is nothing to keep in sync between a manual pre-state and the animation's internal one) rather
  // than being unmounted, so layout/height is stable and nothing pops in structurally. Once
  // revealed, `animate-in fade-in-0 slide-in-from-bottom-2 duration-300` (tw-animate-css utilities,
  // imported in apps/web/src/styles.css) plays exactly once — the `revealed` state never reverts
  // to false, so re-renders don't replay it. Both `animation-duration` (the reveal) and
  // `transition-duration` (the hover lift below) are neutralized under `prefers-reduced-motion` by
  // tokens.css's global block, with no JS timer to gate separately.
  const [drawInRef, revealed] = useDrawIn<HTMLElement>();
  const classes = cn(
    'rounded-xl border border-t-[3px] border-border bg-surface p-4 shadow-card',
    revealed ? 'animate-in fade-in-0 slide-in-from-bottom-2 duration-300' : 'opacity-0',
    // Hover: a lift, not just a bigger shadow — `-translate-y-1` so a hovered card visibly comes off
    // the page. (The transition that makes it *feel* like a lift, not a snap, lives in `mergedStyle`
    // below — see the comment there for why it isn't a `transition-*`/`duration-*` class.)
    'hover:-translate-y-1 hover:shadow-lg',
    ACCENT_BORDER[accent],
    to &&
      'hover:border-border-strong hover:bg-surface-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent',
    className,
  );

  // Tailwind v4's `translate-y-*` utilities animate the standalone `translate` CSS property, not
  // `transform` — which is what the `animate-in`/`slide-in-from-bottom-2` entrance above drives via
  // its keyframes — so the two never fight over the same property on mount-plus-hover.
  //
  // The transition is inline style rather than a `transition`/`duration-*` class so it reads
  // `--duration`/`--ease` directly, the same tokens Ring and ProgressBar already key their own
  // transitions off. A `duration-*` class would work too, but Tailwind's `duration-*` and
  // tw-animate-css's animation-duration fallback both resolve through the same `--tw-duration`
  // custom property — so a second `duration-*` here would silently retune the entrance animation's
  // 300ms (or vice versa) depending on Tailwind's internal stylesheet ordering, not the order these
  // classes are written in. Inline style sidesteps that shared variable entirely. It's still properly
  // neutralized under reduced motion: tokens.css's block sets `transition-duration` with `!important`,
  // which beats this non-important inline style for that one longhand.
  const mergedStyle: CSSProperties = {
    transition:
      'translate var(--duration, 150ms) var(--ease, ease), box-shadow var(--duration, 150ms) var(--ease, ease)',
    ...style,
  };

  const content = (
    <>
      <header className="mb-3 flex flex-wrap items-center gap-2">
        <h3
          className={cn(
            'flex-1',
            variant === 'kpi'
              ? 'text-xs font-semibold uppercase tracking-wide text-fg-subtle'
              : 'font-display text-sm font-bold text-fg',
          )}
        >
          {title}
        </h3>
        {tag && (
          <span className="rounded-md bg-info-subtle px-2 py-0.5 text-[10px] font-semibold text-info">
            {tag}
          </span>
        )}
        {action}
      </header>
      {children}
    </>
  );

  if (to) {
    // `Link` renders an `<a>` (HTMLAnchorElement), narrower than the `<section>` branch's plain
    // HTMLElement — safe to widen back down here since `useDrawIn`'s own code only ever touches
    // generic `Element` members (IntersectionObserver's `observe`/`disconnect`) on `.current`.
    return (
      <Link
        ref={drawInRef as RefObject<HTMLAnchorElement | null>}
        to={to}
        className={classes}
        style={mergedStyle}
        {...rest}
      >
        {content}
      </Link>
    );
  }

  return (
    <section ref={drawInRef} className={classes} style={mergedStyle} {...rest}>
      {content}
    </section>
  );
}
